import { memo, useEffect, useRef } from "react";
import clsx from "clsx";
import type { useForm } from "hooks";
import { Checkbox } from "ui/forms/formik-elements";
import { FormField, type OnboardingBusinessInformation } from "./types";

type Props = Pick<
    ReturnType<typeof useForm<OnboardingBusinessInformation>>,
    | 'setFieldValue'
    | 'setFieldTouched'
    | 'handleBlur'
    | 'values'
    | 'errors'
> & {
    readonly targetField: FormField.YearsUnderOwnership;
    readonly name?: FormField.UseSinceIncorporationDate;
    readonly sourceField?: FormField.IncorporationDate;
};

const UseSinceIncorporationDate = ({
    values,
    errors,
    targetField,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    name = FormField.UseSinceIncorporationDate,
    sourceField = FormField.IncorporationDate
}: Props) => {
    const checked = values[name];
    const sourceFieldValue = values[sourceField];

    const repository = {
        calculateAndSet: (value: boolean) => {
            setFieldValue(
                targetField,
                clsx(value && Math.abs(sourceFieldValue!.diff(new Date(), 'years')))
            );

            setTimeout(() => setFieldTouched(targetField, true, true));
        }
    };
    const repositoryRef = useRef(repository);
    repositoryRef.current = repository;

    const handleFieldValueChange = (name: string, value: boolean, shouldValidate?: boolean) => {
        setFieldValue(name, value, shouldValidate);

        repositoryRef.current.calculateAndSet(value);
    };

    const isSourceFieldsValid = (
        sourceFieldValue &&
        !errors[sourceField]
    );

    useEffect(() => {
        if (!isSourceFieldsValid || !checked) {
            return;
        }

        repositoryRef.current.calculateAndSet(true);
    }, [
        sourceFieldValue,
        isSourceFieldsValid,
        checked
    ]);

    return (
        <Checkbox
            setFieldValue={handleFieldValueChange}
            setFieldTouched={setFieldTouched}
            label='Since incorporation'
            handleBlur={handleBlur}
            CheckboxProps={{
                inputProps: {
                    'aria-label': 'Since incorporation'
                },
                name,
                checked,
                disabled: !isSourceFieldsValid
            }}
        />
    );
};

export default memo(UseSinceIncorporationDate);
